import { HeroActivityLine } from '@/modules/hero-activity/components/hero-activity-line';
import { heroActivityCopy } from '@/modules/hero-activity/utils/hero-activity-copy';
import { type GameState, GamePhase, type BuffType, type Town } from '@/game/types';
import { useUiClock } from '@/hooks/useUiClock';
import { t, useT, useLocale } from '@/i18n';
import { enemyFamilyLabel } from '@/i18n/contentLabels';
import type { HeroResponse } from '@/network/api';
import { HPBar } from './HPBar';
import { BuffBar } from './BuffBar';
import { BuffStatusStrip } from './BuffStatusStrip';
import { DebuffBar } from './DebuffBar';
import { LootPopup } from './LootPopup';
import { InventoryStrip } from './InventoryStrip';
import { GameIcon } from '@/shared/ui/game-icon';
import { woodPanel, metalPanel, copperButton, gameColors } from '@/shared/ui/game-theme';
import { useEffect, useRef } from 'react';
interface HUDProps {
  gameState: GameState;
  connected: boolean;
  towns: Town[];
  onBuffActivate: (type: BuffType) => void;
  buffCooldownEndsAt: Partial<Record<BuffType, number>>;
  onUsePotion?: () => void;
  onHeroUpdated?: (hero: HeroResponse) => void;
  onPurchaseRefill?: (type: BuffType, invoiceUrl: string) => void;
  onOpenHeroSheet: () => void;
  completedQuestCount?: number;
}
export function HUD({ gameState, connected, towns, onBuffActivate, buffCooldownEndsAt, onUsePotion,
  onHeroUpdated, onPurchaseRefill, onOpenHeroSheet, completedQuestCount = 0 }: HUDProps) {
  const { locale } = useLocale();
  const { hero, enemy, phase, lastVictoryLoot } = gameState;
  const nowMs = useUiClock(100);
  const tr = useT();
  const activity = heroActivityCopy(hero?.currentActivity, towns, locale);
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const top = topRef.current;
    const surface = top?.closest<HTMLElement>('.th-game');
    if (!top || !surface || typeof ResizeObserver === 'undefined') return;
    const update = () => surface.style.setProperty('--th-hud-clearance', `${Math.ceil(top.getBoundingClientRect().height + 20)}px`);
    const observer = new ResizeObserver(update);
    observer.observe(top);
    update();
    return () => { observer.disconnect(); surface.style.removeProperty('--th-hud-clearance'); };
  }, []);
  const debuffsLive = hero?.debuffs.map(d => ({ ...d, remainingMs:
    d.expiresAtMs != null ? Math.max(0, d.expiresAtMs - nowMs) : d.remainingMs,
  })).filter(d => d.remainingMs > 0) ?? [];
  return <div className="th-hud-shell">
    <div className="th-hud-top" ref={topRef}>
      {!hero && <div style={{ ...woodPanel, padding: 14, color: gameColors.muted }}>{locale === 'ru' ? 'Загрузка героя…' : 'Loading hero…'}</div>}
      {hero && <div className="th-hud-hero-stack">
        <section style={{ ...woodPanel, padding: '8px 10px' }} aria-label={locale === 'ru' ? 'Состояние героя' : 'Hero status'}>
          <div className="th-hud-row">
            <span className="th-level" aria-label={`${locale === 'ru' ? 'Уровень' : 'Level'} ${hero.level}`}>{hero.level}</span>
            <div className="th-hud-health"><HPBar current={hero.hp} max={hero.maxHp} color={gameColors.health} height={20} showText /></div>
            <span className="th-gold" aria-label={`${locale === 'ru' ? 'Золото' : 'Gold'}: ${hero.gold}`}><GameIcon name="coins" size={15} />{hero.gold.toLocaleString()}</span>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button type="button" className="th-hud-button" style={copperButton} onClick={onOpenHeroSheet}
                aria-label={completedQuestCount > 0 ? `${tr.hero}. ${t(tr.heroSheetQuestBadgeAria, { count: completedQuestCount })}` : tr.hero}>{tr.hero}</button>
              {completedQuestCount > 0 && <span style={{ position: 'absolute', right: -5, top: -6, padding: '1px 4px', background: '#665137', color: '#f0d5a3', border: '1px solid #ba9564', borderRadius: 3, fontSize: 10, pointerEvents: 'none' }} aria-hidden>{completedQuestCount > 99 ? '99+' : completedQuestCount}</span>}
            </div>
            <button type="button" className="th-hud-button" style={{ ...copperButton, color: gameColors.healing, minWidth: 29 }} disabled={hero.potions <= 0 || !onUsePotion} onClick={onUsePotion}
              aria-label={`${locale === 'ru' ? 'Зелье лечения' : 'Healing potion'}: ${hero.potions}`}><GameIcon name="flask" size={15} />{hero.potions}</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 5 }}>
            <BuffStatusStrip buffs={hero.activeBuffs} nowMs={nowMs} /><DebuffBar debuffs={debuffsLive} />
            <HeroActivityLine current={activity.current} next={activity.next} connected={connected} language={locale === 'ru' ? 'ru' : 'en'} />
          </div>
        </section>
        <InventoryStrip hero={hero} lastLoot={lastVictoryLoot} />
      </div>}
      {enemy && (phase === GamePhase.Fighting || phase === GamePhase.Dead) && <section className="th-hud-enemy" style={{ ...metalPanel, marginTop: 7, padding: '7px 10px', width: 'min(250px,100%)', marginLeft: 'auto', marginRight: 'auto' }} aria-label={locale === 'ru' ? 'Противник' : 'Enemy'}>
        <div style={{ color: gameColors.danger, fontSize: 12, marginBottom: 5 }}>{enemyFamilyLabel(locale, enemy.enemySlug, enemy.name)}</div>
        <HPBar current={enemy.hp} max={enemy.maxHp} color={gameColors.health} height={16} showText />
      </section>}
    </div>
    <LootPopup loot={gameState.loot} />
    {hero && <div className="th-buff-dock"><BuffBar buffs={hero.activeBuffs} cooldownEndsAt={buffCooldownEndsAt} buffCharges={hero.buffCharges} buffsLocked={phase === GamePhase.Dead} nowMs={nowMs} onActivate={onBuffActivate} onPurchaseRefill={onPurchaseRefill} onHeroUpdated={onHeroUpdated} /></div>}
  </div>;
}
